import React, { useState, useMemo, useRef, useEffect } from 'react';
import { X, Check, Calendar, Search, ChevronDown } from 'lucide-react';
import LocationSelector from '@/components/LocationSelector';
import MultiSelectDropdown from '@/components/MultiSelectDropdown';

// Searchable Incident Dropdown Component
const SearchableIncidentDropdown = ({ incidents, selectedIncidentId, onSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Filter incidents based on search term
  const filteredIncidents = useMemo(() => {
    if (!searchTerm.trim()) return incidents;
    
    const lowerSearch = searchTerm.toLowerCase();
    return incidents.filter(incident => {
      const incidentId = incident.id ? `INC-${incident.id.slice(-8).toUpperCase()}` : '';
      const incidentType = incident.incident_type || '';
      return (
        incidentId.toLowerCase().includes(lowerSearch) ||
        incidentType.toLowerCase().includes(lowerSearch)
      );
    });
  }, [incidents, searchTerm]);

  // Get selected incident display text
  const selectedIncident = incidents.find(i => i.id === selectedIncidentId);
  const displayText = selectedIncident 
    ? `INC-${selectedIncident.id.slice(-8).toUpperCase()} - ${selectedIncident.incident_type || 'Unknown'}`
    : 'All Incidents (No family filter)';

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm bg-white text-left focus:outline-none focus:ring-blue-500 focus:border-blue-500 flex items-center justify-between"
      >
        <span className="block truncate text-sm">{displayText}</span>
        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${isOpen ? 'transform rotate-180' : ''}`} />
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
          {/* Search Input */}
          <div className="p-2 border-b border-gray-200 sticky top-0 bg-white">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search incidents..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto max-h-60">
            {/* Clear option */}
            <button
              type="button"
              onClick={() => {
                onSelect('');
                setIsOpen(false);
                setSearchTerm('');
              }}
              className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 ${
                !selectedIncidentId ? 'bg-blue-50 font-medium' : ''
              }`}
            >
              All Incidents (No family filter)
            </button>

            {/* Filtered incidents */}
            {filteredIncidents.length > 0 ? (
              filteredIncidents.map((incident) => {
                const incidentDisplay = `INC-${incident.id.slice(-8).toUpperCase()} - ${incident.incident_type || 'Unknown'}`;
                const isSelected = incident.id === selectedIncidentId;
                
                return (
                  <button
                    key={incident.id}
                    type="button"
                    onClick={() => {
                      onSelect(incident.id);
                      setIsOpen(false);
                      setSearchTerm('');
                    }}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-blue-50 flex items-center justify-between ${
                      isSelected ? 'bg-blue-50 font-medium' : ''
                    }`}
                  >
                    <span className="truncate">{incidentDisplay}</span>
                    {isSelected && <Check className="h-4 w-4 text-blue-600 flex-shrink-0 ml-2" />}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-6 text-sm text-gray-500 text-center">
                No incidents found matching "{searchTerm}"
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const FilterModal = ({ 
  isOpen, 
  onClose, 
  incidents = [],
  incidentClassifications = [],
  filters,
  onFiltersChange,
  onReset,
  onApply
}) => {
  if (!isOpen) return null;

  // Helper functions (stable, no dependencies on parent state)
  const getIncidentTypeName = (typeId) => {
    if (!typeId || incidentClassifications.length === 0) return 'Unknown Type';
    const classification = incidentClassifications.find(c => c.id === typeId);
    return classification?.incident_type || 'Unknown Type';
  };

  const getEventName = (typeId, eventId) => {
    return eventId && eventId.trim() ? eventId : 'N/A';
  };

  const getTriggerName = (typeId, eventId, triggerId) => {
    return triggerId && triggerId.trim() ? triggerId : 'N/A';
  };

  // Memoized filter options (stable calculation)
  const filterOptions = useMemo(() => {
    const incidentsArray = Array.isArray(incidents) ? incidents : [];
    
    return {
      incidentTypes: (() => {
        const types = incidentsArray.map(incident => getIncidentTypeName(incident.type_id))
          .filter(type => type && type !== 'Unknown Type');
        return [...new Set(types)].sort();
      })(),
      exerciseTypes: (() => {
        const types = incidentsArray
          .map(incident => incident.exercise_classification)
          .filter(Boolean);
        return [...new Set(types)].sort();
      })(),
      smartEvents: (() => {
        if (filters.incidentType.length === 0) return [];
        const matchingIncidents = incidentsArray.filter(incident => {
          const incidentTypeName = getIncidentTypeName(incident.type_id);
          return filters.incidentType.includes(incidentTypeName);
        });
        const events = matchingIncidents.map(incident => 
          getEventName(incident.type_id, incident.event_id)
        ).filter(event => event && event !== 'N/A');
        return [...new Set(events)].sort();
      })(),
      smartTriggers: (() => {
        if (filters.event.length === 0) return [];
        const matchingIncidents = incidentsArray.filter(incident => {
          const incidentTypeName = getIncidentTypeName(incident.type_id);
          const eventName = getEventName(incident.type_id, incident.event_id);
          return filters.incidentType.includes(incidentTypeName) && 
                 filters.event.includes(eventName);
        });
        const triggers = matchingIncidents.map(incident => 
          getTriggerName(incident.type_id, incident.event_id, incident.trigger_id)
        ).filter(trigger => trigger && trigger !== 'N/A');
        return [...new Set(triggers)].sort();
      })()
    };
  }, [incidents, filters.incidentType, filters.event, incidentClassifications]);

  // Active filter count (stable calculation)
  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.location.length > 0 && filters.location[0]) count++;
    if (filters.level.length > 0) count++;
    if (filters.status.length > 0) count++;
    if (filters.dateType !== 'all') count++;
    if (filters.incidentType.length > 0) count++;
    if ((filters.exerciseType || []).length > 0) count++;
    if (filters.event.length > 0) count++;
    if (filters.trigger.length > 0) count++;
    if (filters.incidentFamily) count++;
    if (filters.reportKind && filters.reportKind !== 'all') count++;
    if ((filters.incidentId || '').trim()) count++;
    return count;
  }, [filters]);

  // Stable event handlers (no inline functions)
  const handleLocationChange = (locationArray) => {
    onFiltersChange({ ...filters, location: locationArray || [] });
  };

  const handleIncidentTypeChange = (types) => {
    onFiltersChange({
      ...filters,
      incidentType: types,
      event: types.length === 0 ? [] : filters.event,
      trigger: types.length === 0 ? [] : filters.trigger
    });
  };

  const handleEventChange = (events) => {
    onFiltersChange({
      ...filters,
      event: events,
      trigger: events.length === 0 ? [] : filters.trigger
    });
  };

  const handleTriggerChange = (triggers) => {
    onFiltersChange({ ...filters, trigger: triggers });
  };

  const handleExerciseTypeChange = (types) => {
    onFiltersChange({ ...filters, exerciseType: types });
  };

  const handleLevelChange = (levels) => {
    onFiltersChange({ ...filters, level: levels });
  };

  const handleStatusChange = (statuses) => {
    onFiltersChange({ ...filters, status: statuses });
  };

  const handleDateTypeChange = (dateType) => {
    onFiltersChange({ ...filters, dateType });
  };

  const handleStartDateChange = (e) => {
    onFiltersChange({ ...filters, startDate: e.target.value });
  };

  const handleEndDateChange = (e) => {
    onFiltersChange({ ...filters, endDate: e.target.value });
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">Filter Incidents</h3>
          <div className="flex items-center space-x-2">
            {activeFilterCount > 0 && (
              <button 
                className="px-3 py-1 text-sm border border-gray-300 rounded-md hover:bg-gray-50"
                onClick={onReset}
              >
                Reset All
              </button>
            )}
            <button 
              className="p-2 hover:bg-gray-100 rounded-md"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="space-y-6">
          {/* Incident ID search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Incident ID
            </label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                value={filters.incidentId || ''}
                onChange={(e) => onFiltersChange({ ...filters, incidentId: e.target.value })}
                placeholder="e.g. 4063D2FE or INC-2026-07…"
                data-testid="filter-incident-id"
                className="w-full pl-9 pr-8 py-2 border border-gray-300 rounded-md shadow-sm text-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              />
              {(filters.incidentId || '') !== '' && (
                <button
                  type="button"
                  onClick={() => onFiltersChange({ ...filters, incidentId: '' })}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
          </div>

          {/* Real Incident vs Drill Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Report Kind
            </label>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'all', label: 'All' },
                { key: 'real', label: 'Real Incidents' },
                { key: 'drill', label: 'Drills' },
                { key: 'sos', label: 'SOS' }
              ].map((option) => (
                <button
                  key={option.key}
                  className={`px-3 py-1 text-sm rounded-md border ${
                    (filters.reportKind || 'all') === option.key
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => onFiltersChange({ ...filters, reportKind: option.key })}
                >
                  {option.label}
                  {(filters.reportKind || 'all') === option.key && (
                    <Check className="h-3 w-3 ml-1 inline" />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Smart Location Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Location (Multi-Select)
            </label>
            <LocationSelector
              value={filters.location}
              onChange={handleLocationChange}
              mode="multi"
              allowWildcard={false}
              placeholder="Select locations to filter incidents"
              className="w-full"
            />
            {filters.location.length > 0 && (
              <div className="mt-2 flex items-center justify-between">
                <span className="text-sm text-gray-600">
                  {filters.location.length} location(s) selected
                </span>
                <button
                  className="text-red-600 hover:text-red-800 text-sm"
                  onClick={() => handleLocationChange([])}
                >
                  <X className="h-3 w-3 mr-1 inline" />
                  Clear
                </button>
              </div>
            )}
          </div>

          {/* Filter by Incident Family */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Filter by Incident Family (Overrides other filters)
            </label>
            <SearchableIncidentDropdown
              incidents={incidents}
              selectedIncidentId={filters.incidentFamily}
              onSelect={(incidentId) => onFiltersChange({ ...filters, incidentFamily: incidentId })}
            />
            {filters.incidentFamily && (
              <div className="mt-2 p-2 bg-yellow-50 border border-yellow-200 rounded-md">
                <p className="text-xs text-yellow-800">
                  <strong>Note:</strong> Family filter is active. This will show the selected incident and all its related incidents (children, grandchildren, etc.) in a tree view. Other filters will be ignored.
                </p>
                <button
                  className="mt-2 text-red-600 hover:text-red-800 text-sm font-medium"
                  onClick={() => onFiltersChange({ ...filters, incidentFamily: '' })}
                >
                  <X className="h-3 w-3 mr-1 inline" />
                  Clear Family Filter
                </button>
              </div>
            )}
          </div>

          {/* Smart Incident Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Incident Type (Smart Selection)
            </label>
            <MultiSelectDropdown
              options={filterOptions.incidentTypes}
              selectedValues={filters.incidentType}
              onChange={handleIncidentTypeChange}
              placeholder="Select incident types..."
              className="w-full"
            />
          </div>

          {/* Smart Event Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Event (Filtered by Incident Type)
            </label>
            <MultiSelectDropdown
              options={filterOptions.smartEvents}
              selectedValues={filters.event}
              onChange={handleEventChange}
              placeholder={filters.incidentType.length === 0 ? "Select incident type first..." : "Select events..."}
              className="w-full"
            />
            {filters.incidentType.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Select incident types above to see available events
              </p>
            )}
          </div>

          {/* Smart Trigger Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Trigger (Filtered by Event)
            </label>
            <MultiSelectDropdown
              options={filterOptions.smartTriggers}
              selectedValues={filters.trigger}
              onChange={handleTriggerChange}
              placeholder={filters.event.length === 0 ? "Select events first..." : "Select triggers..."}
              className="w-full"
            />
            {filters.event.length === 0 && (
              <p className="text-xs text-gray-500 mt-1">
                Select events above to see available triggers
              </p>
            )}
          </div>

          {/* Exercise Type (Exercise Classification) Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Exercise Type
            </label>
            <MultiSelectDropdown
              options={filterOptions.exerciseTypes}
              selectedValues={filters.exerciseType || []}
              onChange={handleExerciseTypeChange}
              placeholder={filterOptions.exerciseTypes.length === 0 ? "No exercise types available" : "Select exercise types..."}
              className="w-full"
            />
          </div>

          {/* Level Filter */}
          <MultiSelectDropdown
            label="Severity Level"
            options={['High', 'Medium', 'Low']}
            selectedValues={filters.level}
            onChange={handleLevelChange}
            placeholder="Select severity levels..."
            className="w-full"
          />

          {/* Status Filter */}
          <MultiSelectDropdown
            label="Status"
            options={
              filters.reportKind === 'drill'
                ? ['requested', 'active', 'completed', 'resolved']
                : filters.reportKind === 'real'
                  ? ['open', 'in_progress', 'resolved']
                  : ['open', 'in_progress', 'requested', 'active', 'completed', 'resolved']
            }
            selectedValues={filters.status}
            onChange={handleStatusChange}
            placeholder="Select status..."
            className="w-full"
          />

          {/* Date & Time Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Date & Time
            </label>
            <div className="flex flex-wrap gap-2 mb-3">
              {[
                { key: 'all', label: 'All Time' },
                { key: 'today', label: 'Today' },
                { key: 'week', label: 'Last 7 Days' },
                { key: 'month', label: 'Last 30 Days' },
                { key: 'custom', label: 'Custom Range' }
              ].map((option) => (
                <button
                  key={option.key}
                  className={`px-3 py-1 text-sm rounded-md border ${
                    filters.dateType === option.key 
                      ? 'bg-blue-600 text-white border-blue-600' 
                      : 'border-gray-300 hover:bg-gray-50'
                  }`}
                  onClick={() => handleDateTypeChange(option.key)}
                >
                  {option.label}
                  {filters.dateType === option.key && (
                    <Check className="h-3 w-3 ml-1 inline" />
                  )}
                </button>
              ))}
            </div>

            {filters.dateType === 'custom' && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">From Date</label>
                  <input
                    type="date"
                    value={filters.startDate}
                    onChange={handleStartDateChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">To Date</label>
                  <input
                    type="date"
                    value={filters.endDate}
                    onChange={handleEndDateChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm"
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Filter Summary */}
        {activeFilterCount > 0 && (
          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <h4 className="text-sm font-medium text-blue-900 mb-2">Active Filters</h4>
            <div className="flex flex-wrap gap-2">
              {filters.location.length > 0 && filters.location[0] && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-md">
                  Location: {filters.location[0].display_label || filters.location[0].root_label || filters.location[0]}
                </span>
              )}
              {filters.incidentType.map(type => (
                <span key={type} className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded-md">
                  Type: {type}
                </span>
              ))}
              {(filters.exerciseType || []).map(type => (
                <span key={type} className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-md">
                  Exercise: {type}
                </span>
              ))}
              {filters.event.map(event => (
                <span key={event} className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-md">
                  Event: {event}
                </span>
              ))}
              {filters.trigger.map(trigger => (
                <span key={trigger} className="px-2 py-1 bg-teal-100 text-teal-800 text-xs rounded-md">
                  Trigger: {trigger}
                </span>
              ))}
              {filters.level.map(level => (
                <span key={level} className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-md">
                  Level: {level}
                </span>
              ))}
              {filters.status.map(status => (
                <span key={status} className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded-md">
                  Status: {status}
                </span>
              ))}
              {filters.dateType !== 'all' && (
                <span className="px-2 py-1 bg-gray-100 text-gray-800 text-xs rounded-md">
                  Date: {filters.dateType === 'custom' 
                    ? `${filters.startDate || 'Start'} to ${filters.endDate || 'End'}`
                    : filters.dateType.charAt(0).toUpperCase() + filters.dateType.slice(1)
                  }
                </span>
              )}
              {filters.incidentFamily && (
                <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs rounded-md font-semibold">
                  🌳 Family Tree: {incidents.find(i => i.id === filters.incidentFamily)?.id?.slice(-8).toUpperCase() || 'N/A'}
                </span>
              )}
              {filters.reportKind && filters.reportKind !== 'all' && (
                <span className="px-2 py-1 bg-indigo-100 text-indigo-800 text-xs rounded-md">
                  Kind: {filters.reportKind === 'real' ? 'Real Incidents' : 'Drills'}
                </span>
              )}
            </div>
          </div>
        )}

        <div className="flex justify-end mt-6 space-x-3">
          <button 
            className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            onClick={onClose}
          >
            Cancel
          </button>
          <button 
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
            onClick={onApply}
          >
            Apply Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default FilterModal;
